import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import RouterControl from './RouterControl';
import history from './history';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@progress/kendo-theme-default/dist/all.css';
import './css/fsa-design-system.css';
import './fsa-style/fsa-style.scss';
import 'react-notifications/lib/notifications.css';
import { UserProvider } from './components/UserContext/UserContext';
import NotificationProvider from './components/Notification/NotificationProvider';
import { Suspense } from 'react';
import { GlobalLoader } from './newcomponents/GlobalLoader/GlobalLoader';
import withClearCache from './ClearCache';
import { configureAxiosInterceptors } from './utils/helper/axiosInterceptor';
import InfoPopupModal from './newcomponents/InfoPopupModal/InfoPopupModal';
import { AuthProvider } from 'react-oidc-context';
import { jwtDecode } from 'jwt-decode';
import { getSystemConfigDetails, getRedactionConfiguration } from './services/inBasketService';
import { searchPageColumnsAPI } from './pages/Search/SearchServices';
const ClearCacheComponent = withClearCache(MainApp);

function App() {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    configureAxiosInterceptors(setIsLoading);
  }, []);
  return (
    <>
      {isLoading && <GlobalLoader />}
      <ClearCacheComponent />
    </>
  );
}

function MainApp(props) {
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [loginMessage, setLoginMessage] = useState('');

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const gridNoRecords = document.querySelectorAll('.k-grid-norecords');

      gridNoRecords.forEach((noRecordsElement) => {
        noRecordsElement.setAttribute('aria-label', 'No records available');
        noRecordsElement.style.color = '#333';
        noRecordsElement.style.backgroundColor = '#fff';
        noRecordsElement.style.opacity = '1';
        noRecordsElement.style.fontSize = '16px';
      });

      const selectElements = document.querySelectorAll('.k-picker.k-dropdown-list.k-dropdown');

      selectElements.forEach((selectElement) => {
        if (!selectElement.hasAttribute('aria-label')) {
          selectElement.setAttribute('aria-label', 'Page number selection');
        }
        selectElement.style.color = '#333';
        selectElement.style.backgroundColor = '#fff';
        selectElement.style.opacity = '1';
        selectElement.style.width = 'auto';
        selectElement.style.border = '1px solid #ccc';
      });

      const pageSizeDropdowns = document.querySelectorAll('.k-pager-sizes select');

      pageSizeDropdowns.forEach((pageSizeDropdown) => {
        if (!pageSizeDropdown.hasAttribute('aria-label')) {
          pageSizeDropdown.setAttribute('aria-label', 'Select page size');
        }
        pageSizeDropdown.style.color = '#333';
        pageSizeDropdown.style.backgroundColor = '#fff';
        pageSizeDropdown.style.opacity = '1';
        pageSizeDropdown.style.width = 'auto';
        pageSizeDropdown.style.border = '1px solid #ccc';
      });
    });

    // Observe the body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup the observer when the component unmounts
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Function to check sessionStorage values
    const checkSessionStorage = () => {
      const showPopup = sessionStorage.getItem('showInfoPopup') === 'true';
      const message = JSON.parse(window.localStorage.getItem('system_config'))?.loginMessage;

      // Update states as required
      if (showPopup && message) {
        setShowInfoPopup(true);
        setLoginMessage(message);

        // Clear 'showInfoPopup' in sessionStorage to prevent multiple triggers
        sessionStorage.removeItem('showInfoPopup');
      }
    };

    // Run the function on mount
    checkSessionStorage();

    // Add a listener for 'storage' events (for multi-tab support)
    const handleStorageChange = (event) => {
      if (event.key === 'showInfoPopup' || event.key === 'system_config') {
        checkSessionStorage();
      }
    };

    // Add a listener for the custom event
    const handleLoginEvent = (event) => {
      const { showInfoPopup, loginMessage } = event.detail;
      if (showInfoPopup && loginMessage) {
        setShowInfoPopup(true);
        setLoginMessage(loginMessage);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginSuccess', handleLoginEvent);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginSuccess', handleLoginEvent);
    };
  }, []);
  const securityApi = {
    createAuthorizationToken: (accessToken) =>
      fetch(`${window.env.REACT_APP_BACKEND_URL}/security_api/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessToken }),
      }),
  };
  const userInfo = {
    fetchUserInfo: (token) =>
      fetch(`${window.env.REACT_APP_BACKEND_URL}/security_api/security/userinfo`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
  };
  // Function to handle closing the modal
  const handleClosePopup = () => {
    setShowInfoPopup(false);
    sessionStorage.setItem('showInfoPopup', 'false');
    setLoginMessage('');
  };
  const fetchSystemFolderData = async (payloadData) => {
    await searchPageColumnsAPI(payloadData).then(async (response) => {
      sessionStorage.setItem('SystemColumns', JSON.stringify(response?.data));
      let columnNewArray = [
        {
          metaId: 0,
          field: 'SELECTED_FIELD',
          title: 'Select',
        },
      ];
      await response?.data?.map((column, idx) => {
        columnNewArray.push({
          metaId: '_id',
          field: 'key.object_specific.' + column.name.replace(' ', ''),
          title: column.name,
          dataType: 'text',
          showMenuIcon: 'true',
          sortType: '',
          data: column,
        });
        if (response.data.length === idx + 1) {
          columnNewArray.push({
            metaId: idx + 1,
            field: 'VIEW_DETAILS',
            title: '',
          });
        }
        sessionStorage.setItem('systemFolderOptions', JSON.stringify(columnNewArray));
      });
    });
  };
  const onSigninCallback = async (user) => {
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
      const response = await securityApi.createAuthorizationToken(user.access_token);

      const decoded = jwtDecode(user.id_token);

      sessionStorage.setItem('user', JSON.stringify(user));
      if (!response.ok) {
        throw new Error('Authorization token creation failed');
      }

      const authJwt = await response.text();
      if (authJwt) {
        sessionStorage.setItem('authorizationToken', authJwt);
      }

      const payload = JSON.parse(atob(authJwt.split('.')[1]));
      sessionStorage.setItem('permissions', JSON.stringify(payload['https://brillient.net/permissions'] || []));
      sessionStorage.setItem('groups', JSON.stringify(payload['groups'] || []));
      sessionStorage.setItem('userId', JSON.stringify(payload['userId'] || []));
      await new Promise((resolve) => setTimeout(resolve, 50));
      await getRedactionConfiguration(window.env.REACT_APP_CLIENT_CONTEXT_ID);

      await getSystemConfigDetails(window.env.REACT_APP_CLIENT_CONTEXT_ID);

      await fetchSystemFolderData();

      window.location.href = '/inbasketpage';
    } catch (e) {
      console.error('Login failed', e);
      window.location.replace('/login?error=auth_failed');
    }
  };
  const oidcConfig = {
    authority: window.env.REACT_APP_IDP_SPA_URL,
    client_id: window.env.REACT_APP_IDP_SPA_CLIENT_ID,
    redirect_uri: window.env.REACT_APP_IDP_SPA_REDIRECT_URL,
    post_logout_redirect_uri: window.location.origin + '/login',
    scope: window.env.REACT_APP_IDP_SPA_OAUTH_SCOPE,
    response_type: 'code',
    loadUserInfo: false,
    onSigninCallback: onSigninCallback,
  };
  return (
    <AuthProvider {...oidcConfig}>
      <UserProvider>
        <NotificationProvider>
          <div className="App">
            <Suspense
              fallback={
                <div>
                  <GlobalLoader />
                </div>
              }
            >
              <Router history={history}>
                <RouterControl />
              </Router>
            </Suspense>
            {showInfoPopup && loginMessage && <InfoPopupModal show={showInfoPopup} onClose={handleClosePopup} message={loginMessage} />}
          </div>
        </NotificationProvider>
      </UserProvider>
    </AuthProvider>
  );
}

export default App;
